import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@design-system';
import { Check, ChevronDown, FileText, Info, Loader2, Play, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  useActiveRunId,
  useRunWizardEvalTestCase,
  useUpdateRunResultOverrides,
  useWizardEvalRules,
  useWizardEvalRun,
  useWizardEvalRunsByTestCaseId,
} from '@/features/wizard-evals/hooks/use-wizard-evals';
import {
  wizardEvalsKeys,
  type ManualOverrides,
  type WizardEvalRule,
  type WizardEvalRunResult,
  type WizardEvalTestCase,
} from '@/features/wizard-evals/api/wizard-evals-api';
import { getRuleById } from '@shared/underwriting';
import { SheetModal } from '@/components/SheetModal';
import { IntakePayloadSheet } from '@/features/wizard-evals/components/IntakePayloadSheet';
import { RunOutputSheet } from '@/features/wizard-evals/components/RunOutputSheet';

interface TestCaseRunPanelProps {
  testCase: WizardEvalTestCase;
}

function MatchPill({
  label,
  expected,
  actual,
  match,
}: {
  label: string;
  expected: string | null;
  actual: string | null;
  match: boolean;
}) {
  if (match) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        <Check className="h-4 w-4 text-green-600" />
        <span className="text-gray-600">{label}:</span>
        <span className="font-medium text-gray-900">{actual ?? expected ?? '—'}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <X className="h-4 w-4 text-red-600" />
      <span className="text-gray-600">{label}:</span>
      <span className="text-gray-600 line-through">{expected ?? '—'}</span>
      <span className="font-medium text-red-700">{actual ?? '—'}</span>
    </span>
  );
}

const BUCKET_TOOLTIPS = {
  pos: 'Rules the pipeline flagged that you expected it to flag. Higher is better.',
  neg: 'Rules the pipeline flagged that you did NOT expect. The pipeline over-flagged.',
  warn: 'Rules you expected the pipeline to flag but it missed. The pipeline under-flagged.',
};

function BucketBadge({
  label,
  count,
  rawCount,
  tone,
  tooltip,
}: {
  label: string;
  count: number;
  rawCount: number;
  tone: 'pos' | 'neg' | 'warn';
  tooltip: string;
}) {
  const toneClasses = {
    pos: 'bg-green-50 border-green-200 text-green-900',
    neg: 'bg-red-50 border-red-200 text-red-900',
    warn: 'bg-orange-50 border-orange-200 text-orange-900',
  }[tone];
  const tooltipText =
    count !== rawCount ? `${tooltip}\n\nAdjusted from raw ${rawCount} → ${count}.` : tooltip;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex cursor-help items-center gap-1.5 rounded border px-2 py-1 text-xs',
            toneClasses,
          )}
        >
          <span className="font-medium">{label}</span>
          <span className="font-mono">
            {count}
            {count !== rawCount && (
              <span className="ml-1 text-[10px] text-gray-600">(raw {rawCount})</span>
            )}
          </span>
          <Info className="h-3 w-3 opacity-60" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs whitespace-pre-line">{tooltipText}</TooltipContent>
    </Tooltip>
  );
}

type OverrideState = 'raw' | 'reclassified' | 'ignored';

function chipClasses(state: OverrideState, tone: 'pos' | 'neg' | 'warn'): string {
  if (state === 'reclassified') {
    return 'border-green-400 bg-green-50 text-green-900';
  }
  if (state === 'ignored') {
    return 'border-gray-300 bg-gray-100 text-gray-500 line-through';
  }
  return {
    pos: 'border-gray-300 bg-white text-gray-700',
    neg: 'border-red-300 bg-red-50 text-red-800',
    warn: 'border-orange-300 bg-orange-50 text-orange-800',
  }[tone];
}

function RuleTooltipBody({ id, rule }: { id: string; rule?: WizardEvalRule }) {
  const catalogEntry = getRuleById(id);
  if (!rule && !catalogEntry) {
    return (
      <div className="max-w-xs text-xs">
        <p className="font-mono font-semibold">{id}</p>
        <p className="mt-1 italic text-gray-300">Not in rule catalog</p>
      </div>
    );
  }
  return (
    <div className="max-w-xs space-y-1.5 text-xs">
      <p className="font-mono font-semibold">{rule?.ruleId ?? id}</p>
      {rule?.ruleText && <p className="whitespace-pre-line leading-snug">{rule.ruleText}</p>}
      {catalogEntry?.keywords?.length ? (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-300">Keywords</p>
          <p className="leading-snug text-gray-100">{catalogEntry.keywords.join(', ')}</p>
        </div>
      ) : null}
      {catalogEntry?.yamlSection && (
        <p className="font-mono text-[10px] text-gray-300">{catalogEntry.yamlSection}</p>
      )}
      <p className="text-[10px] uppercase tracking-wide text-gray-300">
        {rule?.carrier ?? catalogEntry?.carrier}
        {(rule?.category ?? catalogEntry?.category)
          ? ` · ${rule?.category ?? catalogEntry?.category}`
          : ''}
        {rule && !rule.isActive ? ' · inactive' : ''}
      </p>
    </div>
  );
}

interface RuleChipMenuProps {
  id: string;
  state: OverrideState;
  tone: 'neg' | 'warn';
  rule?: WizardEvalRule;
  onReclassify: (id: string) => void;
  onIgnore: (id: string) => void;
  onReset: (id: string) => void;
  disabled?: boolean;
}

function RuleChipMenu({
  id,
  state,
  tone,
  rule,
  onReclassify,
  onIgnore,
  onReset,
  disabled,
}: RuleChipMenuProps) {
  const reclassifyLabel = tone === 'neg' ? 'Mark as correct (TP)' : 'Was caught (TP)';
  const ignoreLabel = tone === 'neg' ? 'Ignore (out of scope)' : 'Ignore (not required)';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <DropdownMenu>
            <DropdownMenuTrigger
              asChild
              disabled={disabled}
            >
              <button
                type="button"
                className={cn(
                  'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[11px] disabled:cursor-not-allowed',
                  chipClasses(state, tone),
                )}
              >
                {state === 'reclassified' && <Check className="h-3 w-3 text-green-700" />}
                <span>{id}</span>
                <ChevronDown className="h-3 w-3 opacity-50" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                disabled={state === 'reclassified'}
                onSelect={() => onReclassify(id)}
              >
                {reclassifyLabel}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={state === 'ignored'}
                onSelect={() => onIgnore(id)}
              >
                {ignoreLabel}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={state === 'raw'}
                onSelect={() => onReset(id)}
              >
                Reset
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
      >
        <RuleTooltipBody
          id={id}
          rule={rule}
        />
      </TooltipContent>
    </Tooltip>
  );
}

function RuleIdList({
  ids,
  tone,
  chipState,
  getRule,
  onReclassify,
  onIgnore,
  onReset,
  disabled,
}: {
  ids: string[];
  tone: 'pos' | 'neg' | 'warn';
  chipState?: (id: string) => OverrideState;
  getRule?: (id: string) => WizardEvalRule | undefined;
  onReclassify?: (id: string) => void;
  onIgnore?: (id: string) => void;
  onReset?: (id: string) => void;
  disabled?: boolean;
}) {
  if (ids.length === 0) {
    return <p className="text-[11px] text-gray-600">—</p>;
  }
  const interactive = tone !== 'pos' && !!onReclassify && !!onIgnore && !!onReset;
  return (
    <div className="flex flex-wrap gap-1">
      {ids.map(id => {
        const rule = getRule?.(id);
        if (interactive) {
          const state = chipState?.(id) ?? 'raw';
          return (
            <RuleChipMenu
              key={id}
              id={id}
              state={state}
              tone={tone as 'neg' | 'warn'}
              rule={rule}
              onReclassify={onReclassify!}
              onIgnore={onIgnore!}
              onReset={onReset!}
              disabled={disabled}
            />
          );
        }
        return (
          <Tooltip key={id}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'inline-flex cursor-help items-center rounded border px-1.5 py-0.5 font-mono text-[11px]',
                  chipClasses('raw', tone),
                )}
              >
                {id}
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="start"
            >
              <RuleTooltipBody
                id={id}
                rule={rule}
              />
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

function overridesToBody(o: ManualOverrides | null | undefined) {
  return {
    fpReclassified: o?.fpReclassified ?? [],
    fpIgnored: o?.fpIgnored ?? [],
    fnAccepted: o?.fnAccepted ?? [],
    fnIgnored: o?.fnIgnored ?? [],
    note: o?.note ?? '',
  };
}

function moveToBucket(
  overrides: ManualOverrides | null | undefined,
  ruleId: string,
  bucket:
    | keyof Pick<ManualOverrides, 'fpReclassified' | 'fpIgnored' | 'fnAccepted' | 'fnIgnored'>
    | null,
): ReturnType<typeof overridesToBody> {
  const body = overridesToBody(overrides);
  body.fpReclassified = body.fpReclassified.filter(x => x !== ruleId);
  body.fpIgnored = body.fpIgnored.filter(x => x !== ruleId);
  body.fnAccepted = body.fnAccepted.filter(x => x !== ruleId);
  body.fnIgnored = body.fnIgnored.filter(x => x !== ruleId);
  if (bucket) body[bucket] = [...body[bucket], ruleId];
  return body;
}

export function TestCaseRunPanel({ testCase }: TestCaseRunPanelProps) {
  const queryClient = useQueryClient();
  const { runId, setRunId } = useActiveRunId(testCase.testCaseId);
  const runMutation = useRunWizardEvalTestCase(testCase.testCaseId);
  const runQuery = useWizardEvalRun(runId);
  const historyQuery = useWizardEvalRunsByTestCaseId(testCase.testCaseId);
  const overridesMutation = useUpdateRunResultOverrides(runId, testCase.testCaseId);
  const [outputOpen, setOutputOpen] = useState(false);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteDirty, setNoteDirty] = useState(false);

  const rulesQuery = useWizardEvalRules();
  const getRule = useMemo(() => {
    const map = new Map((rulesQuery.data ?? []).map(r => [r.ruleId, r] as const));
    return (id: string) => map.get(id);
  }, [rulesQuery.data]);

  const runRow = runQuery.data?.run;
  const result = runQuery.data?.results?.[0];
  const status = runRow?.status;
  const isPolling = !!runId && (status === 'queued' || status === 'running');
  const isFinished = status === 'completed' || status === 'failed';
  const hasExpectedRate = testCase.expectedRateClass != null;

  useEffect(() => {
    setNoteDraft(result?.manualOverrides?.note ?? '');
    setNoteDirty(false);
  }, [result?.runResultId, result?.manualOverrides?.note]);

  const chipState = useMemo(() => {
    const mo = result?.manualOverrides;
    const reclassified = new Set(mo?.fpReclassified ?? []);
    const fpIgnored = new Set(mo?.fpIgnored ?? []);
    const accepted = new Set(mo?.fnAccepted ?? []);
    const fnIgnored = new Set(mo?.fnIgnored ?? []);
    return (id: string): OverrideState => {
      if (reclassified.has(id) || accepted.has(id)) return 'reclassified';
      if (fpIgnored.has(id) || fnIgnored.has(id)) return 'ignored';
      return 'raw';
    };
  }, [result?.manualOverrides]);

  const submitOverrides = (body: ReturnType<typeof overridesToBody>) => {
    if (!result?.runResultId) return;
    overridesMutation.mutate(
      { runResultId: result.runResultId, overrides: body },
      {
        onError: (err: any) => {
          toast.error('Failed to save override', {
            description: err?.message ?? 'Unknown error',
          });
        },
      },
    );
  };

  const handleReclassifyFp = (id: string) =>
    submitOverrides(moveToBucket(result?.manualOverrides, id, 'fpReclassified'));
  const handleIgnoreFp = (id: string) =>
    submitOverrides(moveToBucket(result?.manualOverrides, id, 'fpIgnored'));
  const handleAcceptFn = (id: string) =>
    submitOverrides(moveToBucket(result?.manualOverrides, id, 'fnAccepted'));
  const handleIgnoreFn = (id: string) =>
    submitOverrides(moveToBucket(result?.manualOverrides, id, 'fnIgnored'));
  const handleReset = (id: string) =>
    submitOverrides(moveToBucket(result?.manualOverrides, id, null));

  const handleSaveNote = () => {
    if (!noteDirty || !result?.manualOverrides) {
      const body = overridesToBody(result?.manualOverrides);
      body.note = noteDraft;
      submitOverrides(body);
      setNoteDirty(false);
      return;
    }
    const body = overridesToBody(result.manualOverrides);
    body.note = noteDraft;
    submitOverrides(body);
    setNoteDirty(false);
  };

  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!status) return;
    if (prevStatusRef.current !== status && (status === 'completed' || status === 'failed')) {
      queryClient.invalidateQueries({
        queryKey: wizardEvalsKeys.runsByTestCase(testCase.testCaseId),
      });
    }
    prevStatusRef.current = status;
  }, [status, queryClient, testCase.testCaseId]);

  const handleRun = async () => {
    try {
      const res = await runMutation.mutateAsync();
      setRunId(res.run.runId);
      queryClient.invalidateQueries({
        queryKey: wizardEvalsKeys.runsByTestCase(testCase.testCaseId),
      });
    } catch (err: any) {
      toast.error('Failed to start run', {
        description: err?.message ?? 'Unknown error',
      });
    }
  };

  const buttonLabel = runMutation.isPending
    ? 'Starting…'
    : isPolling
      ? 'Running…'
      : runId
        ? 'Run again'
        : 'Run this case';

  const history = historyQuery.data?.runs ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          onClick={() => setIntakeOpen(true)}
        >
          <FileText className="mr-1 h-4 w-4" />
          View intake data
        </Button>
        <Button
          variant="outline"
          onClick={handleRun}
          disabled={runMutation.isPending || isPolling}
        >
          <Play className="mr-1 h-4 w-4" />
          {buttonLabel}
        </Button>
      </div>

      {historyQuery.isLoading && (
        <div className="flex items-center gap-2 rounded border border-dashed border-gray-300 p-4 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading runs…
        </div>
      )}

      {history.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium uppercase text-gray-600">History ({history.length})</p>
          <ul className="divide-y divide-gray-200 rounded border border-gray-200">
            {history.map(row => {
              const isActive = row.run.runId === runId;
              const runStatus = row.run.status;
              const res = row.result;
              const rowHasExpectedRate = res?.expectedRateClass != null;
              const rowF1 = res?.hasOverrides ? res.effectiveF1 : (res?.ruleF1 ?? null);
              const canOpen = runStatus === 'completed' && !!res;
              const openMetrics = () => {
                setRunId(row.run.runId);
                setMetricsOpen(true);
              };
              const openOutput = () => {
                setRunId(row.run.runId);
                setOutputOpen(true);
              };
              return (
                <li
                  key={row.run.runId}
                  className={cn(
                    'flex items-center justify-between gap-3 px-3 py-2 text-xs',
                    isActive ? 'bg-primary-50' : '',
                  )}
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="font-mono text-[11px] text-gray-700">
                      {new Date(row.run.createdDateTime).toLocaleString()}
                    </span>
                    <span className="truncate text-[11px] text-gray-600">
                      {row.run.triggeredBy}
                      {row.run.modelVersion ? ` · ${row.run.modelVersion}` : ''}
                      {row.run.promptVersion ? ` · prompt ${row.run.promptVersion}` : ''}
                      {row.run.scope !== 'single' ? ` · ${row.run.scope}` : ''}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {runStatus === 'completed' && res && (
                      <>
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px]',
                            res.outcomeMatch
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800',
                          )}
                          title="Outcome match"
                        >
                          {res.outcomeMatch ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <X className="h-3 w-3" />
                          )}
                          outcome
                        </span>
                        {rowHasExpectedRate && (
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px]',
                              res.rateClassMatch
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800',
                            )}
                            title="Rate class match"
                          >
                            {res.rateClassMatch ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <X className="h-3 w-3" />
                            )}
                            rate
                          </span>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1 font-mono text-[10px] text-gray-700">
                              F1 {rowF1 != null ? rowF1.toFixed(2) : '—'}
                              {res.hasOverrides && <Check className="text-primary-dark h-3 w-3" />}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {res.hasOverrides
                              ? `Raw ${res.ruleF1?.toFixed(2) ?? '—'} → reviewed ${res.effectiveF1.toFixed(2)}`
                              : 'F1 (raw resolver)'}
                          </TooltipContent>
                        </Tooltip>
                      </>
                    )}
                    {(runStatus === 'queued' || runStatus === 'running') && (
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-800">
                        {runStatus}
                      </span>
                    )}
                    {runStatus === 'failed' && (
                      <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] text-red-800">
                        failed
                      </span>
                    )}
                    {canOpen && (
                      <>
                        <Button
                          size="sm"
                          className="h-7 px-2 text-[11px]"
                          onClick={openMetrics}
                        >
                          Results
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[11px]"
                          onClick={openOutput}
                        >
                          Analysis
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {isPolling && (
        <p className="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600">
          {status === 'queued' ? 'Queued…' : 'Running pipeline…'} polling every 2s. Typically
          completes in 30–90 seconds.
        </p>
      )}

      {status === 'failed' && (
        <p className="rounded border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          Run failed: {runRow?.errorMessage ?? 'Unknown error'}
        </p>
      )}

      <SheetModal
        open={metricsOpen}
        onClose={() => setMetricsOpen(false)}
        title={`${testCase.name} · results`}
        subTitle=""
        className="sm:min-w-[85%] md:min-w-[640px] lg:min-w-[760px]"
      >
        <div className="flex flex-col gap-5 pb-8 pt-3">
          {!isFinished || !result ? (
            <p className="text-sm text-gray-600">Loading run…</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <MatchPill
                  label="Outcome"
                  expected={result.expectedOutcome}
                  actual={result.actualOutcome}
                  match={result.outcomeMatch}
                />
                {hasExpectedRate && (
                  <MatchPill
                    label="Rate"
                    expected={result.expectedRateClass}
                    actual={result.actualRateClass}
                    match={result.rateClassMatch}
                  />
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1.5 font-mono text-sm text-gray-700">
                      F1 {result.effectiveF1.toFixed(2)}
                      {result.hasOverrides && <Check className="text-primary-dark h-4 w-4" />}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {result.hasOverrides
                      ? `Raw ${result.ruleF1?.toFixed(2) ?? '—'} → reviewed ${result.effectiveF1.toFixed(2)}`
                      : 'F1 (raw resolver)'}
                  </TooltipContent>
                </Tooltip>
                <span className="ml-auto font-mono text-xs text-gray-600">
                  P {result.effectivePrecision.toFixed(2)} · R {result.effectiveRecall.toFixed(2)}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <BucketBadge
                  label="True positives"
                  count={result.effectiveTruePositives.length}
                  rawCount={result.truePositives.length}
                  tone="pos"
                  tooltip={BUCKET_TOOLTIPS.pos}
                />
                <BucketBadge
                  label="False positives"
                  count={result.effectiveFalsePositives.length}
                  rawCount={result.falsePositives.length}
                  tone="neg"
                  tooltip={BUCKET_TOOLTIPS.neg}
                />
                <BucketBadge
                  label="False negatives"
                  count={result.effectiveFalseNegatives.length}
                  rawCount={result.falseNegatives.length}
                  tone="warn"
                  tooltip={BUCKET_TOOLTIPS.warn}
                />
              </div>

              <div>
                <p className="mb-2 text-[11px] uppercase tracking-wide text-gray-600">
                  Flagged rule IDs
                  <span className="ml-1 normal-case text-gray-500">(click a chip to override)</span>
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <p className="mb-1 text-[11px] font-medium uppercase text-green-900">
                      True positives
                    </p>
                    <RuleIdList
                      ids={result.truePositives}
                      tone="pos"
                      getRule={getRule}
                    />
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-medium uppercase text-red-900">
                      False positives
                    </p>
                    <RuleIdList
                      ids={result.falsePositives}
                      tone="neg"
                      chipState={chipState}
                      getRule={getRule}
                      onReclassify={handleReclassifyFp}
                      onIgnore={handleIgnoreFp}
                      onReset={handleReset}
                      disabled={overridesMutation.isPending}
                    />
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-medium uppercase text-orange-900">
                      False negatives
                    </p>
                    <RuleIdList
                      ids={result.falseNegatives}
                      tone="warn"
                      chipState={chipState}
                      getRule={getRule}
                      onReclassify={handleAcceptFn}
                      onIgnore={handleIgnoreFn}
                      onReset={handleReset}
                      disabled={overridesMutation.isPending}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium uppercase text-gray-600">
                  Review note (optional)
                </label>
                <Textarea
                  value={noteDraft}
                  onChange={e => {
                    setNoteDraft(e.target.value);
                    setNoteDirty(true);
                  }}
                  onBlur={() => {
                    if (noteDirty) handleSaveNote();
                  }}
                  placeholder="Why you overrode these — for your future self or teammates."
                  className="min-h-[60px] text-xs"
                />
              </div>
            </>
          )}
        </div>
      </SheetModal>

      {result && (
        <RunOutputSheet
          open={outputOpen}
          onOpenChange={setOutputOpen}
          result={result}
          title={`${testCase.name} · analysis`}
        />
      )}
      <IntakePayloadSheet
        open={intakeOpen}
        onOpenChange={setIntakeOpen}
        intakePayload={testCase.intakePayload}
        title={`${testCase.name} · intake data`}
      />
    </div>
  );
}
